import { useQuery } from "@apollo/client";
import { Box, Modal } from "@mui/material";
import { useEffect, useState } from "react";
import { ExpanderComponentProps } from "react-data-table-component";

import { Button } from "@/components/atoms/Button/styles";
import { StyledDataTable } from "@/components/molecules/StyledDataTable";
import { VoiceMessageList } from "@/components/molecules/VoiceMessageList";

import { ActivityRecord, Playlist } from "../../../../types/api";
import {
  BdlNote,
  ExpandableTextWrapper,
  InlineTextWrapper,
  PlaylistTableWrapper,
  RightHeaderDiv,
  RightStyledH1,
} from "../../styles";
import { ModalStyle } from "../UserInfo/components/AddTreatmentModal/styles";
import { GET_LATEST_PROGRAM } from "../UserInfo/components/TreatmentHistoryTable/queries";
import { columns } from "./columns";
import { CreatePlaylistModal } from "./components/CreatePlaylistModal";
import { SinglePlaylistModal } from "./components/SinglePlaylistModal";
import { DataRow, PlaylistsProps } from "./types";

export const PlaylistsComponent = (props: PlaylistsProps) => {
  const [playlistId, setPlaylistId] = useState<number>(0);
  const [playlistModalOpen, setPlaylistModalOpen] = useState<boolean>(false);
  const handlePlaylistModalClose = () => setPlaylistModalOpen(false);
  const [createPlaylistModalOpen, setCreatePlaylistModalOpen] = useState<boolean>(false);
  const [intensiveProgramModalOpen, setIntensiveProgramModalOpen] = useState<boolean>(false);
  const [modalMessage, setModalMessage] = useState<string>("");

  const [duplicatePlaylist, setDuplicatePlaylist] = useState<Playlist | undefined>();

  const playlistTableData: DataRow[] =
    props?.account?.playlists?.map((playlist): DataRow => {
      const { id, title, isActive, startOn, endOn, playlistVoiceMessages, program } = playlist;

      const playlistDuration = playlistVoiceMessages
        ?.map((singleVoiceMessage) => singleVoiceMessage.voiceMessage?.audioFile?.durationInSeconds)
        ?.reduce((previousValue?, currentValue?) => (previousValue ?? 0) + (currentValue ?? 0), 0);

      const startDate = new Date(startOn?.toString() ?? ""); // Date values are strings in graphql response
      const endDate = new Date(endOn?.toString() ?? "");

      return {
        id: id ?? 0,
        playlistName: `${program}`
          ? `${program?.program_type} Round ${program?.round} ${title}`
          : "",
        startOn: startDate,
        endOn: endDate,
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line no-constant-condition
        numOfActivities: `${playlistVoiceMessages?.length} activities`
          ? `${playlistVoiceMessages?.length} activities`
          : "",
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line no-constant-condition
        numOfPlaybacks: `12 (${playlistDuration} sec)` ? `12 (${playlistDuration} sec)` : "",
        isActive: props.inMaintenance ? false : isActive,
        defaultExpanded: true,
        playlistVoiceMessages: playlist?.playlistVoiceMessages ?? [],
      };
    }) ?? [];

  //send all activity records, which will be later filtered by clicking on single playlist
  const allActivityRecords: ActivityRecord[] =
    props?.account?.callRecords?.flatMap((call) => call?.activities ?? [])?.filter((x) => !!x) ??
    [];

  const ExpandedComponent: React.FC<ExpanderComponentProps<DataRow>> = (playlistTableData) => (
    <ExpandableTextWrapper>
      <InlineTextWrapper>
        <VoiceMessageList
          voiceMessages={
            playlistTableData?.data?.playlistVoiceMessages
              ?.map((pvm) => pvm.voiceMessage)
              // TODO: Fix this the next time the file is edited.
              // eslint-disable-next-line no-extra-boolean-cast
              ?.flatMap((x) => (!!x ? [x] : [])) ?? []
          }
          showIcon={false}
          isDragDisabled={true}
        />
      </InlineTextWrapper>
    </ExpandableTextWrapper>
  );

  const onRowClick = (row: DataRow) => {
    setPlaylistId(row.id);
    setPlaylistModalOpen(true);
  };

  const checkActivePlaylistExpand = (row: DataRow) => {
    if (row.isActive) {
      return (row.defaultExpanded = true);
    } else {
      return (row.defaultExpanded = false);
    }
  };

  const playlists = props.account.playlists;

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function sortPlaylists(playlists: any) {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sortedPlaylists = playlists.slice().sort((a: any, b: any) => {
      const createdDateA = new Date(a.createdOn);
      const createdDateB = new Date(b.createdOn);

      if (createdDateA > createdDateB) {
        return -1;
      } else if (createdDateA < createdDateB) {
        return 1;
      } else {
        const endDateA = new Date(a.endOn);
        const endDateB = new Date(b.endOn);
        return endDateA.getTime() - endDateB.getTime();
      }
    });
    return sortedPlaylists[0];
  }

  const latestPlaylist = sortPlaylists(playlists);

  const treatmentHistory = props.AllProgramsData?.treatmentHistory;

  const {
    data: latestProgramData,
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    loading: latestProgramLoading,
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    error: latestProgramError,
    refetch: latestProgramrefetch,
  } = useQuery(GET_LATEST_PROGRAM, {
    variables: { accountId: props.account.id },
    skip: !treatmentHistory || treatmentHistory.length === 0,
    fetchPolicy: "network-only", // Always fetch fresh data
  });

  // Refetch latest program when treatment history changes
  useEffect(() => {
    if (treatmentHistory && treatmentHistory.length > 0) {
      latestProgramrefetch();
    }
  }, [treatmentHistory?.length, latestProgramrefetch]);

  const handleCreatePlaylistModalOpen = async () => {
    // Check if we have treatment history
    if (!treatmentHistory || treatmentHistory.length === 0) {
      setCreatePlaylistModalOpen(true);
      return;
    }

    // Force refetch to get the latest data
    try {
      const { data: freshData } = await latestProgramrefetch();
      const freshLatestProgram = freshData?.getLatestProgram;

      if (freshLatestProgram) {
        const currentDate = new Date();
        const programEndDate = freshLatestProgram.end_date
          ? new Date(freshLatestProgram.end_date)
          : null;
        const programType = freshLatestProgram.program_type?.toLowerCase();

        // Check if program has ended (end date is in the past)
        const isProgramEnded = programEndDate && programEndDate < currentDate;

        if (programType === "intensive" && isProgramEnded) {
          // Intensive program has ended - show modal to create new or update
          setModalMessage(
            "Your intensive program has ended. Please create a new intensive program or update the current one's end date to create playlists."
          );
          setIntensiveProgramModalOpen(true);
        } else if (programType === "maintenance") {
          // Check if maintenance program is in past, present, or future
          const programStartDate = freshLatestProgram.start_date
            ? new Date(freshLatestProgram.start_date)
            : null;
          const isMaintenanceEnded = programEndDate && programEndDate < currentDate;
          const isMaintenanceFuture = programStartDate && programStartDate > currentDate;

          if (isMaintenanceEnded) {
            // Maintenance program has ended
            const endDateStr = programEndDate.toLocaleDateString("en-US", {
              month: "long",
              day: "numeric",
              timeZone: "UTC",
            });
            setModalMessage(
              `Your maintenance program has ended on ${endDateStr}. Please go to Patient Info and create a new intensive program from add new treatment.`
            );
          } else if (isMaintenanceFuture) {
            // Maintenance program is in the future
            setModalMessage(
              "No active intensive program but there is an upcoming maintenance program found. To proceed with intensive program playlists, please create a new intensive program or adjust the dates of an existing one."
            );
          }
          setIntensiveProgramModalOpen(true);
        } else if (programType === "intensive" && !isProgramEnded) {
          // Active intensive program - can create playlist
          setCreatePlaylistModalOpen(true);
        } else {
          // Default case
          setCreatePlaylistModalOpen(true);
        }
      } else {
        // No program data - default to create playlist
        setCreatePlaylistModalOpen(true);
      }
    } catch (error) {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line no-console
      console.error("Error refetching latest program:", error);
      setCreatePlaylistModalOpen(true);
    }
  };

  const handleCreatePlaylistModalClose = () => {
    setCreatePlaylistModalOpen(false);
    setDuplicatePlaylist(undefined);
  };

  const handleIntensiveProgramModalClose = () => {
    setIntensiveProgramModalOpen(false);
  };

  return (
    <div>
      <RightHeaderDiv>
        {props.inMaintenance && (
          <BdlNote>
            Note: User is currently active in the Maintenance program. Any active playlist set below
            will not be delivered to the user over any call.
          </BdlNote>
        )}
        <RightStyledH1>
          Playlists
          <Button onClick={handleCreatePlaylistModalOpen} disabled={props.inMaintenance}>
            Create New Playlist
          </Button>
        </RightStyledH1>
      </RightHeaderDiv>

      <PlaylistTableWrapper>
        <StyledDataTable
          columns={columns}
          data={playlistTableData ?? []}
          expandableRows
          expandableRowExpanded={checkActivePlaylistExpand}
          expandableRowsComponent={ExpandedComponent}
          onRowClicked={onRowClick}
          // Sort by the fifth column, which is sorted by status, then start date, and/or end date
          defaultSortFieldId={5}
          defaultSortAsc={false}
          customStyles={{
            rows: {
              style: {
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              },
            },
          }}
        />
      </PlaylistTableWrapper>

      <SinglePlaylistModal
        playlists={
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
          props?.account?.playlists?.map((e) => {
            return e.title!;
          })!
        }
        latestProgram={
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
          props?.account?.playlists?.map((e) => {
            return e.program!;
          })!
        }
        open={playlistModalOpen}
        onClose={handlePlaylistModalClose}
        playlistId={playlistId}
        allPVMs={allActivityRecords}
        setDuplicatePlaylist={setDuplicatePlaylist}
        openDuplicate={handleCreatePlaylistModalOpen}
        therapist={props.account.specialist?.firstName}
        accountId={props.account.id}
      />

      <CreatePlaylistModal
        lastPlaylist={latestPlaylist}
        AllProgramsData={props.AllProgramsData}
        ProgramsReftech={props.ProgramsReftech}
        getLatestProgramObject={latestProgramData?.getLatestProgram}
        getLatestProgramrefetch={latestProgramrefetch}
        latestProgram={props?.account?.playlists?.slice(-1)[0]?.program ?? undefined}
        playlists={
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
          props?.account?.playlists?.map((e) => {
            return e.title!;
          })!
        }
        accountId={props.account.id}
        open={createPlaylistModalOpen}
        onClose={handleCreatePlaylistModalClose}
        allPVMs={allActivityRecords}
        duplicatePlaylist={duplicatePlaylist}
        therapist={props.account.specialist?.firstName}
      />

      {intensiveProgramModalOpen && (
        <Modal open={intensiveProgramModalOpen} onClose={handleIntensiveProgramModalClose}>
          <Box sx={ModalStyle} maxWidth={600}>
            <h4>Note: {modalMessage}</h4>
            <div style={{ display: "flex", gap: 2, justifyContent: "end" }}>
              <Button onClick={handleIntensiveProgramModalClose}>Close</Button>
            </div>
          </Box>
        </Modal>
      )}
    </div>
  );
};
